import { matchPath } from 'react-router';
import { call, put, select, take } from 'redux-saga/effects';

import { FETCH_DEALER_FAILURE, FETCH_DEALER_SUCCESS } from 'constants/ActionTypes';
import { PATH } from 'constants/RoutesConstants';
import { TranslationOrigin } from 'constants/translationConstants';
import { putAndWait } from 'modules/app/sagas';
import { scopeToPage } from 'modules/app/sagas/utils';

import { FETCH_APPOINTMENT_SUCCESS } from 'modules/appointments/actionTypes';
import { FETCH_AUDIT_SUCCESS } from 'modules/audits/actionTypes';
import { DETAILS_INITIALIZED } from 'modules/campaigns/actionTypes';
import { CHAT_IS_READY } from 'modules/chat/actionTypes';
import { fetchTranslations } from 'modules/i18n/actions';
import { FETCH_TRANSLATIONS_FAILURE, FETCH_TRANSLATIONS_SUCCESS } from 'modules/i18n/actionTypes';
import { FETCH_KANBAN_INTERVENTIONS, FETCH_INTERVENTION_SUCCESS } from 'modules/interventions/actionTypes';
import { getInterventionMode } from 'modules/interventions/selectors/details';
import { getLocation } from 'modules/router/selectors';

import { getSelectedDealer } from 'selectors/dealerSelectors';

import { getLanguages } from 'selectors/languageSelectors';

import * as actions from '../actions';
import { PREDEFINED_MESSAGE_KEY, PREDEFINED_TITLE_KEY } from '../constants';
import { computeDefaultPredefinedMessage } from '../utils';

const PAGES = [
  PATH.CHAT,
  PATH.SALES_DETAILED,
  PATH.PARTS_DETAILED,
  PATH.AUDITS_DETAILED,
  PATH.CAMPAIGNS_DETAILS,
  PATH.REMINDERS_DETAILED,
  PATH.BODYWORKS_DETAILED,
  PATH.AFTERSALES_DETAILED,
  PATH.PARTS_REMINDER_DETAILED,
  PATH.SELF_SERVICE_KANBAN,
  PATH.KEY_DROP_OFF_APPOINTMENTS_DETAILED,
];

function* getDefaultMessageQueryParams() {
  const { pathname } = yield select(getLocation);
  const dealer = yield select(getSelectedDealer);
  const matchingRoute = PAGES.find(path => matchPath(path.path, pathname) ?? (path.simplePath && matchPath(path.simplePath, pathname)));
  const route = matchPath(matchingRoute.path, pathname) ?? matchPath(matchingRoute.simplePath, pathname);

  if ('appointmentId' in route.params) {
    return { contentKey: PREDEFINED_MESSAGE_KEY.APPOINTMENT, dealerId: dealer.id };
  }

  if ('interventionId' in route.params) {
    const { interventionId } = route.params;
    const mode = yield select(state => getInterventionMode(state, interventionId));
    return { contentKey: PREDEFINED_MESSAGE_KEY.INTERVENTION, interventionId, mode };
  }

  if ('auditId' in route.params) {
    return { contentKey: PREDEFINED_MESSAGE_KEY.AUDIT, auditId: route.params.auditId };
  }

  return { contentKey: PREDEFINED_MESSAGE_KEY.DEFAULT, dealerId: dealer.id };
}

function* getDefaultPredefinedMessage() {
  const languages = yield select(getLanguages);
  const { contentKey, ...params } = yield call(getDefaultMessageQueryParams);

  yield put(fetchTranslations({ keys: [PREDEFINED_TITLE_KEY, contentKey], languages, origin: TranslationOrigin.FO, ...params }));
  const { error, payload } = yield take([FETCH_TRANSLATIONS_SUCCESS, FETCH_TRANSLATIONS_FAILURE]);

  return !error ? computeDefaultPredefinedMessage(payload, PREDEFINED_TITLE_KEY, contentKey) : undefined;
}

function* getDealer() {
  const dealer = yield select(getSelectedDealer);
  if (!dealer) {
    const { payload, error } = yield take([FETCH_DEALER_SUCCESS, FETCH_DEALER_FAILURE]);
    return !error ? payload : undefined;
  }
  return dealer;
}

function* onPage() {
  yield take([FETCH_APPOINTMENT_SUCCESS, CHAT_IS_READY, FETCH_INTERVENTION_SUCCESS, FETCH_AUDIT_SUCCESS, FETCH_KANBAN_INTERVENTIONS, DETAILS_INITIALIZED]);
  const dealer = yield call(getDealer);
  if (dealer) {
    // Fetch dealer and group predefined message
    const { payload } = yield putAndWait(actions.fetchFindByDealerOrGroup(dealer.id, dealer.groupId ?? dealer.group?.id));

    // Fetch default value
    const defaultPredefinedMessage = yield call(getDefaultPredefinedMessage, dealer);

    // Union of dealer/group and default messages
    const predefinedMessages = [
      ...(payload?.content ?? []),
      ...(defaultPredefinedMessage ? [defaultPredefinedMessage] : []),
    ];

    if (predefinedMessages.length > 0) {
      yield put(
        actions.fetchFindByDealerOrGroupSuccess({ content: predefinedMessages }, { dealerId: dealer.id })
      );
    }
  }
  yield put(actions.translationsPageReady());
}

export default function* bootloader() {
  yield scopeToPage(PAGES, onPage);
}
